/**
 * DocumentLibrarySection
 *
 * Collapsible section for browsing all matter documents.
 * Uses simplified document browser with Enterprise Compact design.
 * Supports controlled open state to persist across view mode changes.
 */
import { ChevronDown, FolderOpen } from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

import { SimpleDocumentLibrary } from './SimpleDocumentLibrary';

interface DocumentLibrarySectionProps {
  matterId: string;
  onDocumentClick?: (artifactId: string) => void;
  /** Controlled open state */
  isOpen?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
}

export const DocumentLibrarySection = ({
  matterId,
  onDocumentClick,
  isOpen,
  onOpenChange,
}: DocumentLibrarySectionProps) => {
  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 overflow-hidden">
        {/* Header */}
        <CollapsibleTrigger
          className={cn(
            'flex items-center justify-between w-full px-3 py-2',
            'bg-white dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800',
            'hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors',
          )}
        >
          <div className="flex items-center gap-1.5">
            <FolderOpen className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
            <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
              Document Library
            </h3>
          </div>
          <ChevronDown className="h-3 w-3 text-zinc-400 dark:text-zinc-500 transition-transform [[data-state=open]>&]:rotate-180" />
        </CollapsibleTrigger>

        {/* Content */}
        <CollapsibleContent>
          <div className="p-2">
            <SimpleDocumentLibrary
              matterId={matterId}
              onDocumentClick={onDocumentClick}
            />
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
